import { getMenuItems } from '@countries/ro/app/modules/userProfile/menuItems';
import {
    ShellLocalConfig,
    SiteLingualityKind,
} from '@src/config/shell/localConfigTypes';
import {
    NavigationItem,
    NavigationKind,
} from '@app/router/types';
import { RouteName } from '@app/router/constants/RouteName';
import {
    GamingRoutes,
    ShopsRoutes,
    SocialRoutes,
    TicketRoutes,
    Locale,
} from '@superbet-group/web.types';
import { TimeFilterSlug } from '@store/modules/ui/shared/timeFilter/utils';
import {
    DepositType,
    WithdrawalType,
} from '@core/models/user/enums';
import { isoCode2ToIsoCode } from '@sportOfferFederation/flags/flagsDefinitions';
import { PromotionHubTab } from '@src/app/promotionHub/shared/types/promotionHubTab';

export const shellLocalConfig: Partial<ShellLocalConfig> = {
    responsibleGamingPageElements: {
        DepositLimit: true,
        LossLimit: false,
        SessionLimit: false,
        RealityCheck: false,
        TotalWagerLimit: true,
        SelfExclusion: true,
        TimeOut: true,
        AccountClosure: true,
    },
    footer: {
        viberNumber: '',
        hasLegalSectionTitle: false,
        hasAgeWarning: true,
        hasDisclaimer: true,
        hasOnjnLogo: true,
        hasRombetLogo: true,
        hasIbiaLogo: true,
        hasJocResponsabilLogo: true,
        hasBigLogos: true,
        hasEighteenPlus: true,
        hasEighteenPlusCrossed: false,
        hasEighteenPlusNoLink: false,
        hasMgaLogo: false,
        hasBrazilGovLogo: false,
        hasBagoLogo: false,
        hasFpsLogo: false,
        hasGamblingHelpLogo: false,
        hasAlwaysPlayLegallyLogo: false,
        hasPlayResponsiblyText: false,
        hasGamblingTherapyLogo: false,
        hasResponsibleGamingLinks: false,
    },
    bonusWallet: {
        awardFreeSpinsByCount: true,
        categories: {
            CardGames: false,
            Sport: true,
            Virtuals: true,
            Casino: true,
            Retail: true,
            Bingo: true,
        },
    },
    deposit: {
        areMultipleOnlineMethods: true,
        paymentLogos: {
            aircash: [
                {
                    logoName: 'finance-aircash',
                    width: 100,
                },
            ],
            paySafeCard: [
                {
                    logoName: 'finance-paysafecard',
                    width: 100,
                },
            ],
            online: [
                {
                    logoName: 'finance-apple-pay',
                    height: 18,
                    width: 29,
                },
                {
                    logoName: 'finance-mastercard',
                    height: 18,
                    width: 25,
                },
                {
                    logoName: 'finance-visa',
                    height: 18,
                    width: 36,
                },
                {
                    logoName: 'finance-abon',
                    height: 18,
                    width: 50,
                },
                {
                    logoName: 'finance-skrill',
                    height: 18,
                    width: 36,
                },
                {
                    logoName: 'finance-revolut',
                    height: 18,
                    width: 54,
                },
                {
                    logoName: 'finance-paysafecard',
                    height: 18,
                    width: 85,
                },
                {
                    logoName: 'finance-okto-cash',
                    height: 18,
                    width: 94,
                },
            ],
            onlinePiq: [
                {
                    logoName: 'finance-mastercard',
                    height: 18,
                    width: 25,
                },
                {
                    logoName: 'finance-visa',
                    height: 18,
                    width: 36,
                },
            ],
        },
    },
    withdrawal: {
        paymentLogos: {
            paySafeCard: [
                {
                    logoName: 'finance-paysafecard',
                    width: 100,
                },
            ],
        },
    },
    siteLanguageConfig: {
        kind: SiteLingualityKind.SingleLingual,
        locales: [
            {
                name: 'RO',
                value: Locale.Romanian,
                flag: isoCode2ToIsoCode.RO,
            },
            {
                name: 'EN',
                value: Locale.English,
                flag: isoCode2ToIsoCode.GB,
            },
        ],
    },
    urlLocale: Locale.Romanian,
    getAccountSidebarMenuItems: getMenuItems,
    hasShopsFederation: true,
    hasSocialFederation: true,
    seonConfig: {
        isComtradeFlowSupported: true,
        login: {
            isServiceEnabled: true,
        },
        register: {
            isServiceEnabled: true,
        },
        transactions: {
            isServiceEnabled: true,
            shouldSendWithTransaction: false,
            shouldSendSeparateCall: true,
        },
    },
    accountRecovery: true,
    isEmailContactShown: false,
    navigationDestinations: {
        [NavigationItem.Logo]: {
            kind: NavigationKind.SingleDomain,
            location: {
                name: RouteName.App.Home,
            },
        },
        [NavigationItem.Sport]: {
            kind: NavigationKind.SingleDomain,
            location: {
                name: RouteName.Sport.PrematchOffer,
            },
        },
        [NavigationItem.Live]: {
            kind: NavigationKind.SingleDomain,
            location: {
                name: RouteName.Sport.CategoryPerTime,
                params: {
                    timeFilter: TimeFilterSlug.Live,
                },
            },
        },
        [NavigationItem.Casino]: {
            kind: NavigationKind.SingleDomain,
            location: {
                name: GamingRoutes.MyCasinoHome,
            },
        },
        [NavigationItem.CasinoLive]: {
            kind: NavigationKind.SingleDomain,
            location: {
                name: GamingRoutes.MyCasinoLive,
            },
        },
        [NavigationItem.Bingo]: {
            kind: NavigationKind.SingleDomain,
            location: {
                name: GamingRoutes.MyCasinoBingo,
            },
        },
        [NavigationItem.Virtual]: {
            kind: NavigationKind.SingleDomain,
            location: {
                name: GamingRoutes.MyCasinoVirtuals,
            },
        },
        [NavigationItem.Lottery]: {
            kind: NavigationKind.SingleDomain,
            location: {
                name: RouteName.Lottery.Offer,
            },
        },
        [NavigationItem.News]: {
            kind: NavigationKind.SingleDomain,
            location: {
                name: RouteName.News.Feed,
            },
        },
        [NavigationItem.Shops]: {
            kind: NavigationKind.SingleDomain,
            location: {
                name: ShopsRoutes.Shops,
            },
        },
        [NavigationItem.Promotions]: {
            kind: NavigationKind.SingleDomain,
            location: {
                name: RouteName.GrowthMarketing.PromotionsInfo,
            },
        },
        [NavigationItem.VerifyTicket]: {
            kind: NavigationKind.SingleDomain,
            location: {
                name: TicketRoutes.VerifyTicket,
            },
        },
        [NavigationItem.Social]: {
            kind: NavigationKind.SingleDomain,
            location: {
                name: SocialRoutes.Home,
            },
        },
        [NavigationItem.MyTickets]: {
            kind: NavigationKind.SingleDomain,
            location: {
                name: TicketRoutes.Tickets,
            },
        },
        [NavigationItem.PromotionHub]: {
            kind: NavigationKind.SingleDomain,
            location: {
                name: RouteName.BonusAndComms.PromotionHub,
            },
        },
    },
    bottomNavbarItems: [
        NavigationItem.Sport,
        NavigationItem.Live,
        NavigationItem.Casino,
        NavigationItem.MyTickets,
    ],
    bonus: {
        isPromotionHubEntryPointVisible: true,
        availablePromoHubTabs: [
            PromotionHubTab.Offers,
            PromotionHubTab.InProgress,
            PromotionHubTab.Bonuses,
            PromotionHubTab.History,
        ],
    },
    defaultDepositTypes: {
        [DepositType.Online]: true,
        [DepositType.OnlinePix]: false,
        [DepositType.Aircash]: true,
        [DepositType.BankTransfer]: true,
        [DepositType.Shop]: true,
        [DepositType.PaySafeCard]: true,
        [DepositType.PaySpot]: false,
        [DepositType.Transaks]: false,
        [DepositType.Bancontact]: false,
        [DepositType.PaymentIQ]: false,
    },
    defaultWithdrawalTypes: {
        [WithdrawalType.Online]: true,
        [WithdrawalType.OnlinePix]: false,
        [WithdrawalType.BankTransfer]: true,
        [WithdrawalType.Instant]: true,
        [WithdrawalType.Shop]: true,
        [WithdrawalType.PaySafeCard]: true,
        [WithdrawalType.Transaks]: false,
        [WithdrawalType.PaymentIQ]: false,
    },
    isSportalNewsEnabled: false,

    sponsoredEntities: [
        {
            name: 'Superliga',
            imageSrc: 'romania-sponsorship-superliga',
            url: 'https://lpf.ro/liga-1',
        },
        {
            name: 'La Liga',
            imageSrc: 'romania-sponsorship-laliga',
            url: 'https://www.laliga.com/en-GB',
        },
        {
            name: 'FC Rapid',
            imageSrc: 'romania-sponsorship-rapid',
            url: 'https://www.fcrapid.ro/',
        },
        {
            name: 'FC Dinamo',
            imageSrc: 'romania-sponsorship-dinamo-bucuresti',
            url: 'https://dinamo1948.ro/',
        },
        {
            name: 'Farul Constanta',
            imageSrc: 'romania-sponsorship-farul-constanta',
            url: 'https://www.farulconstanta.com/',
        },
        {
            name: 'U Cluj',
            imageSrc: 'romania-sponsorship-universitatea-cluj',
            url: 'https://www.fcucluj.ro/',
        },
        {
            name: 'Transylvania Open',
            imageSrc: 'romania-sponsorship-transylvania-open',
            url: 'https://transylvaniaopen.ro/',
        },
        {
            name: 'Superliga Feminina',
            imageSrc: 'romania-sponsorship-superliga-feminina',
            url: 'https://www.facebook.com/superligafemininaromania/',
            width: 36,
        },
        {
            name: 'CS Dinamo',
            imageSrc: 'romania-sponsorship-cs-dinamo',
            url: 'http://www.csdinamo.eu/',
        },
        {
            name: 'CS Rapid',
            imageSrc: 'romania-sponsorship-cs-rapid',
            url: 'https://csrapid.ro/',
        },
        {
            name: 'DFS Fighting',
            imageSrc: 'romania-sponsorship-dynamite-fighting-show',
            url: 'https://dynamitefighting.com/',
        },
        {
            name: 'Sao Paulo FC',
            imageSrc: 'brasil-sponsorship-sao-paulo-fc',
            url: 'https://www.saopaulofc.net/spfc',
        },
        {
            name: 'Fluminense',
            imageSrc: 'brasil-sponsorship-fluminense',
            url: 'https://www.fluminense.com.br/site/',
        },
        {
            name: 'Gloria Bistrita',
            imageSrc: 'romania-sponsorship-gloria',
            url: 'https://gloria2018.ro/',
        },
        {
            name: 'SuperChess',
            imageSrc: 'romania-sponsorship-super-chess',
            url: 'https://www.superchess.ro/',
            width: 32,
        },
        {
            name: 'Lech Poznań',
            imageSrc: 'poland-sponsorship-lech-poznan',
            url: 'https://www.lechpoznan.pl',
        },
        {
            name: 'SS Politehnica Timisoara',
            imageSrc: 'romania-sponsorship-poli-tehnica',
            url: 'https://www.sspolitehnica.ro',
        },
    ],
    isUsernameDisplayed: true,
    failedLoginAttemptsBeforeForgotPassword: 2,
};
